import type { MenuProps } from 'antd';
import {
  MediaDeviceKind,
  MediaType,
  type UseQuickVOResult,
  type RoomUser,
} from '@/contexts/chat/scripts/hooks/useQuickVO/types';
import { ChatMessagePayloadType } from '@/contexts/chat/scripts/hooks/useChatMessages/types';
import styles from './index.module.scss';

const VideoOperate = ({
  open,
  children,
  devicesData,
  callType,
  roomUser,
  publish,
  inactiveTracks,
  getMediaDeviceKind,
  setMediaDeviceKind,
}: {
  open: boolean;
  children: ReactNode;
  devicesData: MediaDeviceInfo[];
  callType?: ChatMessagePayloadType;
  roomUser: RoomUser | undefined;
  publish: UseQuickVOResult['publish'];
  inactiveTracks: UseQuickVOResult['inactiveTracks'];
  getMediaDeviceKind: UseQuickVOResult['getMediaDeviceKind'];
  setMediaDeviceKind: UseQuickVOResult['setMediaDeviceKind'];
}) => {
  const [activeKey, setActiveKey] = useState('');
  const [devices, setDevices] = useState<MenuProps['items']>([]);

  const defaultItems: MenuProps['items'] = useMemo(
    () => [
      {
        key: 'camera',
        label: <div className={styles.menuLabel}>Camera</div>,
        disabled: true,
      },
      {
        type: 'divider',
      },
    ],
    []
  );

  const getVideoInputDevices = useCallback(
    async (devicesData: MediaDeviceInfo[]) => {
      const defaultDevices = [
        {
          kind: 'videoinput',
          deviceId: 'default',
          label: 'Default',
        },
      ];
      const newDevicesData = defaultDevices.concat(devicesData);
      if (activeKey && open) {
        if (newDevicesData.find((device) => device.deviceId === activeKey)) {
          await setMediaDeviceKind(MediaDeviceKind.VIDEO_INPUT, activeKey)?.catch(
            (_) => {
              message.warning('Device switching failed');
            }
          );
          inactiveTracks(MediaType.MICROPHONECAMERA_VIDEO, true);
        } else {
          inactiveTracks(MediaType.MICROPHONECAMERA_VIDEO, false);
        }
      }

      setDevices([
        ...defaultItems,
        ...newDevicesData!.map((device) => ({
          kind: device.kind,
          key: device.deviceId,
          label: <div className={styles.menuLabel}>{device.label}</div>,
        })),
      ]);
    },
    [activeKey, open, defaultItems, setMediaDeviceKind, inactiveTracks]
  );

  useEffect(() => {
    if (devicesData.length) {
      getVideoInputDevices(devicesData);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [devicesData.length]);

  useEffect(() => {
    if (callType === ChatMessagePayloadType.MEDIA_AUDIO_CALL) {
      setActiveKey('');
    } else if (open) {
      const deviceId = getMediaDeviceKind(MediaDeviceKind.VIDEO_INPUT);
      if (devicesData.find((device) => device.deviceId === deviceId)) {
        setActiveKey(deviceId!);
      } else {
        setMediaDeviceKind(MediaDeviceKind.VIDEO_INPUT, 'default');
        setActiveKey('default');
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [getMediaDeviceKind, open, callType, setMediaDeviceKind]);

  return (
    <Dropdown
      overlayClassName={styles.dropdownwrapper}
      menu={{
        items: devices,
        activeKey,
        onClick: async (item) => {
          try {
            setActiveKey(item.key);
            await setMediaDeviceKind(MediaDeviceKind.VIDEO_INPUT, item.key);
            if (
              !roomUser?.tracks?.find(
                (track) => track.mediaType === MediaType.MICROPHONECAMERA_VIDEO
              )
            ) {
              await publish(MediaType.MICROPHONECAMERA_VIDEO);
            }
            if (!roomUser?.callActionMap.microphoneCamera_video) {
              inactiveTracks(MediaType.MICROPHONECAMERA_VIDEO, true);
            }
          } catch (error) {
            message.warning('Device switching failed');
            console.log(error, 'error');
          }
        },
      }}
      placement="top"
      getPopupContainer={(e) => e.parentElement as HTMLDivElement}
    >
      <div>{children}</div>
    </Dropdown>
  );
};
export default VideoOperate;
